import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Handshake, Briefcase, FileText, X, ArrowLeft, Sparkles, UserRound, BadgeCheck } from 'lucide-react';

const CHUNK_SIZE = 512 * 1024;
const EMPTY_BROKER = { first_name: '', last_name: '', company: '', email: '', phone_work: '' };

export const AddDealDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('type'); // 'type' | 'details'
  const [dealType, setDealType] = useState(null);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [file, setFile] = useState(null);
  const [codeName, setCodeName] = useState('');
  const [nameMeta, setNameMeta] = useState({ topic: '', definition: '' });
  const [shuffling, setShuffling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [broker, setBroker] = useState(EMPTY_BROKER);
  const [brokerExisting, setBrokerExisting] = useState(null);
  const [brokerSearch, setBrokerSearch] = useState('');
  const [brokerResults, setBrokerResults] = useState([]);

  const reset = () => {
    setStep('type'); setDealType(null); setName(''); setWebsite(''); setFile(null);
    setCodeName(''); setNameMeta({ topic: '', definition: '' }); setProgress(0);
    setBroker(EMPTY_BROKER); setBrokerExisting(null); setBrokerSearch(''); setBrokerResults([]);
  };

  const handleOpenChange = (o) => {
    if (!o && submitting) return;
    if (!o) reset();
    onOpenChange(o);
  };

  const shuffle = async () => {
    setShuffling(true);
    try {
      const res = await api.generateProjectName();
      setCodeName(res.data.random_project_name);
      setNameMeta({ topic: res.data.topic || '', definition: res.data.definition || '' });
    } catch { toast.error('Could not generate a name'); }
    setShuffling(false);
  };

  const pickType = (t) => {
    setDealType(t);
    setStep('details');
    if (!codeName) shuffle();
  };

  useEffect(() => {
    if (dealType !== 'brokered' || brokerExisting || !brokerSearch.trim()) { setBrokerResults([]); return; }
    const t = setTimeout(() => {
      api.getContacts(brokerSearch.trim(), '')
        .then(r => setBrokerResults((r.data.contacts || []).slice(0, 5)))
        .catch(() => setBrokerResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [brokerSearch, dealType, brokerExisting]);

  const setB = (k) => (e) => setBroker(b => ({ ...b, [k]: e.target.value }));

  const saveBrokerContact = async (projectId) => {
    try {
      if (brokerExisting) {
        await api.linkContactToProject(brokerExisting.id, projectId);
      } else {
        await api.createContact({ ...broker, type: 'Broker', project_ids: [projectId], force: true });
      }
    } catch {
      toast.warning('Deal created, but the broker contact could not be saved — please add it on the Contacts page');
    }
  };

  const handleSubmit = async () => {
    const nameOptional = dealType === 'brokered' && !!file;
    if (!name.trim() && !nameOptional) { toast.error('Please enter the company name'); return; }
    if (dealType === 'brokered' && !brokerExisting && !broker.last_name.trim() && !broker.company.trim()) {
      toast.error('Please add the broker contact — select an existing one or enter at least a last name or company');
      return;
    }
    setSubmitting(true);
    try {
      let projectId;
      const willAutofill = !!file || !!website.trim();
      if (file) {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const initRes = await api.initChunkedProject(file.name, totalChunks);
        const uploadId = initRes.data.upload_id;
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
          let attempts = 0;
          while (attempts < 3) {
            try { await api.uploadChunk(uploadId, i, chunk); break; }
            catch (chunkErr) {
              attempts++;
              if (attempts >= 3) throw chunkErr;
              await new Promise(r => setTimeout(r, 1000 * attempts));
            }
          }
          setProgress(Math.round(((i + 1) / totalChunks) * 100));
        }
        const res = await api.completeChunkedProject(uploadId, name.trim(), dealType, website.trim(), codeName, true);
        projectId = res.data.id;
      } else {
        const res = await api.createProjectSimple(name.trim(), dealType, website.trim(), codeName, willAutofill);
        projectId = res.data.id;
      }
      if (dealType === 'brokered') await saveBrokerContact(projectId);
      toast.success(willAutofill ? 'Deal created — AI is filling the deal overview' : 'Deal created');
      handleOpenChangeForce();
      navigate(`/projects/${projectId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deal');
    }
    setSubmitting(false);
  };

  const handleOpenChangeForce = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="add-deal-dialog" className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            {step === 'details' && (
              <button onClick={() => setStep('type')} className="text-muted-foreground hover:text-foreground" data-testid="add-deal-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            Add Deal
            {step === 'details' && (
              <span className={`text-[10px] font-mono uppercase tracking-wider border rounded px-1.5 py-0.5 ${dealType === 'proprietary' ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : 'text-blue-400 border-blue-500/40 bg-blue-500/10'}`}>
                {dealType}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 'type' ? (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">What kind of deal is this?</p>
            <button onClick={() => pickType('proprietary')} data-testid="deal-type-proprietary"
              className="w-full flex items-start gap-3 p-4 border border-border rounded-sm hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors text-left">
              <Handshake className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Proprietary Deal</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Sourced directly — no intermediary. Off-market opportunity.</p>
              </div>
            </button>
            <button onClick={() => pickType('brokered')} data-testid="deal-type-brokered"
              className="w-full flex items-start gap-3 p-4 border border-border rounded-sm hover:border-blue-500/50 hover:bg-blue-500/5 transition-colors text-left">
              <Briefcase className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Brokered Deal</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Via M&A advisor / broker — usually with a teaser document.</p>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">
                Company Name {dealType === 'brokered' ? <span className="normal-case text-foreground/30">(optional with teaser)</span> : '*'}
              </p>
              <Input autoFocus value={name} onChange={e => setName(e.target.value)}
                placeholder={dealType === 'brokered' ? 'Leave empty for anonymous teasers' : 'e.g. Müller Maschinenbau GmbH'}
                className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" data-testid="add-deal-name-input" />
              {dealType === 'brokered' && !name.trim() && (
                <p className="text-[10px] text-muted-foreground mt-1" data-testid="add-deal-noname-hint">
                  No company name? Upload the teaser — the codename is used and AI extracts industry, revenue and region automatically.
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Company Website</p>
              <Input value={website} onChange={e => setWebsite(e.target.value)}
                placeholder="https://..."
                className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" data-testid="add-deal-website-input" />
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">{dealType === 'brokered' ? 'Teaser / Document (PDF)' : 'Document (PDF)'}</p>
              {file ? (
                <div className="flex items-center gap-2 p-2.5 bg-secondary border border-border rounded-sm">
                  <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                  <span className="text-xs text-foreground truncate flex-1">{file.name}</span>
                  <span className="text-[10px] text-muted-foreground font-mono shrink-0">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-red-400" data-testid="add-deal-remove-file">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-border rounded-sm cursor-pointer hover:border-blue-500/50 hover:bg-blue-500/5 transition-colors">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Click to upload a PDF</span>
                  <input type="file" accept=".pdf,application/pdf" className="hidden" data-testid="add-deal-file-input"
                    onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ''; }} />
                </label>
              )}
            </div>

            {/* Broker contact — required for brokered deals, feeds the Inbound ranking */}
            {dealType === 'brokered' && (
              <div className="bg-secondary/40 border border-blue-600/30 rounded-sm p-3 space-y-2" data-testid="add-deal-broker-section">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-[10px] text-blue-400/80 uppercase tracking-wider font-bold">Broker Contact *</p>
                  <span className="text-[9px] text-muted-foreground">saved to Contacts &amp; the Inbound ranking</span>
                </div>
                {brokerExisting ? (
                  <div className="flex items-center gap-2 p-2 bg-secondary border border-border rounded-sm" data-testid="broker-selected">
                    <UserRound className="w-4 h-4 text-sky-400 shrink-0" />
                    <span className="text-xs text-foreground truncate">
                      {[brokerExisting.first_name, brokerExisting.last_name].filter(Boolean).join(' ') || brokerExisting.company}
                    </span>
                    {brokerExisting.company && <span className="text-[10px] text-muted-foreground truncate">· {brokerExisting.company}</span>}
                    {brokerExisting.has_photo && brokerExisting.photo_verified && <BadgeCheck className="w-3 h-3 text-emerald-400 shrink-0" />}
                    <button onClick={() => setBrokerExisting(null)} className="ml-auto text-muted-foreground hover:text-red-400 shrink-0" data-testid="broker-clear-btn">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Input value={brokerSearch} onChange={e => setBrokerSearch(e.target.value)}
                        placeholder="Search existing contacts… (or enter a new one below)"
                        className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" data-testid="broker-search-input" />
                      {brokerResults.length > 0 && (
                        <div className="absolute inset-x-0 top-full mt-1 bg-card border border-border rounded-sm shadow-lg z-20 overflow-hidden" data-testid="broker-search-results">
                          {brokerResults.map((c, i) => (
                            <button key={c.id} onClick={() => { setBrokerExisting(c); setBrokerSearch(''); setBrokerResults([]); }}
                              data-testid={`broker-result-${i}`}
                              className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-secondary/60 text-left transition-colors">
                              <UserRound className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="text-xs text-foreground truncate">{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}</span>
                              {c.company && <span className="text-[10px] text-muted-foreground truncate">· {c.company}</span>}
                              {c.type && <span className="ml-auto text-[9px] font-mono text-sky-300 border border-sky-600/40 bg-sky-600/10 rounded px-1 py-0.5 shrink-0">{c.type}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={broker.first_name} onChange={setB('first_name')} placeholder="First name"
                        className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" data-testid="broker-firstname-input" />
                      <Input value={broker.last_name} onChange={setB('last_name')} placeholder="Last name *"
                        className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" data-testid="broker-lastname-input" />
                      <Input value={broker.company} onChange={setB('company')} placeholder="Advisory firm"
                        className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" data-testid="broker-company-input" />
                      <Input value={broker.email} onChange={setB('email')} placeholder="Email" type="email"
                        className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs" data-testid="broker-email-input" />
                      <Input value={broker.phone_work} onChange={setB('phone_work')} placeholder="Phone"
                        className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs col-span-2" data-testid="broker-phone-input" />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Who brought this deal? The contact is created automatically and linked to the new deal.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Code name (like the claim Namer) */}
            <div className="bg-secondary/40 border border-border rounded-sm p-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Project Code Name</p>
                <button onClick={shuffle} disabled={shuffling} data-testid="add-deal-shuffle-name"
                  className="text-muted-foreground hover:text-blue-400 transition-colors" title="Shuffle name">
                  <RefreshCw className={`w-3.5 h-3.5 ${shuffling ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <p className="font-heading text-lg text-foreground mt-1" data-testid="add-deal-codename">
                {codeName || (shuffling ? '…' : '—')}
              </p>
              {nameMeta.definition && (
                <p className="text-[10px] text-muted-foreground mt-0.5">{nameMeta.topic && <span className="text-blue-400/70">{nameMeta.topic} — </span>}{nameMeta.definition}</p>
              )}
            </div>
            {(file || website.trim()) && (
              <p className="text-[11px] text-emerald-400/80 flex items-start gap-1.5">
                <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                AI will read the {file && website.trim() ? 'teaser and website' : file ? 'document' : 'website'} and pre-fill the deal overview automatically.
              </p>
            )}
            {submitting && file && (
              <div>
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 font-mono">Uploading… {progress}%</p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={submitting}
                className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting || (!name.trim() && !(dealType === 'brokered' && file))} data-testid="add-deal-submit"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
                {submitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Create Deal
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
